import { formatAmount, LIVE_BREAK_MAX_BOXES, LIVE_BREAK_TITLE_MAX } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import { Switch } from "@loot-os/ui/components/switch";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import type { ReactNode } from "react";

import { amountInputLabel, amountInputStep, useCurrency } from "../lib/currency";
import type {
  DraftBox,
  LiveBreakDraft,
  LiveBreakDraftIssues,
  RefusedLiveBreakBoxes,
} from "../lib/liveBreakDraft";
import { LIVE_BREAK_BOX_INELIGIBLE_LABELS, moveId } from "../lib/liveBreakStaff";
import { ratePercentText } from "../lib/liveBreaks";
import { LiveBreakBoxPicker } from "./LiveBreakBoxPicker";

/** One text field with its hint, or what is wrong with it, beneath. */
function TextField({
  id,
  label,
  hint,
  issue,
  children,
}: {
  id: string;
  label: string;
  hint: ReactNode;
  issue: string | undefined;
  children: ReactNode;
}) {
  return (
    <Field data-invalid={issue ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {children}
      {issue ? <FieldError>{issue}</FieldError> : <FieldDescription>{hint}</FieldDescription>}
    </Field>
  );
}

/** One on/off setting: the switch, its label and what it does. */
function SwitchField({
  id,
  label,
  description,
  checked,
  disabled,
  issue,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  issue?: string;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <Field orientation="horizontal" data-invalid={issue ? true : undefined}>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
      <FieldContent>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        {issue ? (
          <FieldError>{issue}</FieldError>
        ) : (
          <FieldDescription>{description}</FieldDescription>
        )}
      </FieldContent>
    </Field>
  );
}

/**
 * The live break's boxes in order: move, remove, and a picker that adds one at the end. A box the
 * last save was refused for carries "Can't be sold" and the server's reason, until it is removed.
 */
function BoxListField({
  idPrefix,
  boxes,
  refused,
  disabled,
  issue,
  onChange,
}: {
  idPrefix: string;
  boxes: readonly DraftBox[];
  refused: RefusedLiveBreakBoxes;
  disabled: boolean;
  issue: string | undefined;
  onChange: (boxes: DraftBox[]) => void;
}) {
  const currency = useCurrency();
  const ids = boxes.map((box) => box.id);
  const move = (id: string, by: -1 | 1) => {
    const order = moveId(ids, id, by);
    onChange(order.map((boxId) => boxes.find((box) => box.id === boxId) as DraftBox));
  };
  const full = boxes.length >= LIVE_BREAK_MAX_BOXES;
  return (
    <Field data-invalid={issue ? true : undefined}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FieldLabel id={`${idPrefix}-boxes-label`}>Boxes</FieldLabel>
        {!disabled && (
          <div className="w-full sm:w-64">
            <LiveBreakBoxPicker
              excludeIds={new Set(ids)}
              disabled={full}
              triggerLabel={full ? `At most ${LIVE_BREAK_MAX_BOXES} boxes` : "Add a box…"}
              onPick={(box) => onChange([...boxes, box])}
            />
          </div>
        )}
      </div>
      {boxes.length === 0 ? (
        <p className="rounded-md border px-3 py-4 text-center text-sm text-muted-foreground">
          No boxes yet.
        </p>
      ) : (
        <ol aria-labelledby={`${idPrefix}-boxes-label`} className="divide-y rounded-md border">
          {boxes.map((box, index) => {
            const refusal = refused.get(box.id);
            return (
              <li key={box.id} className="flex items-center gap-3 px-3 py-2">
                <BoxImage
                  src={box.imageUrl}
                  className="size-9 shrink-0 rounded border object-contain"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm">{box.name}</span>
                  {refusal && (
                    <span className="text-xs text-destructive">
                      {LIVE_BREAK_BOX_INELIGIBLE_LABELS[refusal]}
                    </span>
                  )}
                </span>
                {refusal && <Badge variant="destructive">Can't be sold</Badge>}
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  {formatAmount(box.priceCents, currency)}
                </span>
                {!disabled && (
                  <span className="flex shrink-0 items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Move ${box.name} up`}
                      disabled={index === 0}
                      onClick={() => move(box.id, -1)}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Move ${box.name} down`}
                      disabled={index === boxes.length - 1}
                      onClick={() => move(box.id, 1)}
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${box.name}`}
                      onClick={() => onChange(boxes.filter((other) => other.id !== box.id))}
                    >
                      <X />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {issue ? (
        <FieldError>{issue}</FieldError>
      ) : (
        <FieldDescription>
          {`1 to ${LIVE_BREAK_MAX_BOXES}, in the order shoppers see them. A box added must be in a category the store offers (Storefront → Categories) and at or above the minimum box price. One already chosen keeps selling when the minimum is raised, but not once its category is switched off.`}
        </FieldDescription>
      )}
    </Field>
  );
}

const NO_REFUSED_BOXES: RefusedLiveBreakBoxes = new Map();

/**
 * A live break's settings as staff edit them: New house live break (`mode="create"`) and a live
 * break's own page (`mode="edit"`). A house live break shows its end date and no rate or Auto
 * Mode switch (it is always in Auto Mode); a hosted one its rate. Read-only while `disabled`;
 * `endsAtOnly` leaves the end date alone editable (a house live break Draining, whose one edit is
 * moving or clearing its end). `refusedBoxes` marks the boxes the last save was refused for.
 */
export function LiveBreakSettingsFields({
  idPrefix,
  mode,
  isHouse,
  draft,
  issues,
  maxRateBps,
  disabled,
  endsAtOnly = false,
  refusedBoxes = NO_REFUSED_BOXES,
  onChange,
}: {
  idPrefix: string;
  mode: "create" | "edit";
  isHouse: boolean;
  draft: LiveBreakDraft;
  issues: LiveBreakDraftIssues;
  maxRateBps: number;
  disabled: boolean;
  endsAtOnly?: boolean;
  refusedBoxes?: RefusedLiveBreakBoxes;
  onChange: (patch: Partial<LiveBreakDraft>) => void;
}) {
  const currency = useCurrency();
  const id = (name: string) => `${idPrefix}-${name}`;
  // Every field but the end date follows this; the end date follows `disabled` alone.
  const locked = disabled || endsAtOnly;
  return (
    <div className="grid gap-6">
      <TextField
        id={id("title")}
        label="Title"
        hint={`Shown on the live break and in the directory; ${LIVE_BREAK_TITLE_MAX} characters at most.`}
        issue={issues.title}
      >
        <Input
          id={id("title")}
          value={draft.title}
          maxLength={LIVE_BREAK_TITLE_MAX}
          disabled={locked}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </TextField>

      <BoxListField
        idPrefix={idPrefix}
        boxes={draft.boxes}
        refused={refusedBoxes}
        disabled={locked}
        issue={issues.boxes}
        onChange={(boxes) => onChange({ boxes })}
      />

      <div className="grid gap-4">
        <SwitchField
          id={id("public")}
          label="Listed"
          description="Shown in the storefront's directory. A private live break opens only from its link."
          checked={draft.isPublic}
          disabled={locked}
          onCheckedChange={(isPublic) => onChange({ isPublic })}
        />
        <SwitchField
          id={id("restricted")}
          label="Only its own boxes"
          description="Sells only the boxes above; off, any eligible box at or above the minimum price sells too."
          checked={draft.isRestrictedToLiveBreakBoxes}
          disabled={locked}
          onCheckedChange={(isRestrictedToLiveBreakBoxes) =>
            onChange({ isRestrictedToLiveBreakBoxes })
          }
        />
        {mode === "edit" && (
          <SwitchField
            id={id("accepting")}
            label="Taking purchases"
            description="Off stops new purchases while the queue keeps playing."
            checked={draft.isAcceptingPurchases}
            disabled={locked}
            onCheckedChange={(isAcceptingPurchases) => onChange({ isAcceptingPurchases })}
          />
        )}
        {mode === "edit" && !isHouse && (
          <SwitchField
            id={id("auto")}
            label="Auto Mode"
            description="The server runs the queue and the host's Next and Open now are refused. Off also clears a pause."
            checked={draft.isAutoMode}
            disabled={locked}
            onCheckedChange={(isAutoMode) =>
              onChange(isAutoMode ? { isAutoMode } : { isAutoMode, isPaused: false })
            }
          />
        )}
        {mode === "edit" && (
          <SwitchField
            id={id("paused")}
            label="Paused"
            description="Finishes the Opening on stage, then starts no new one until resumed. Purchases are unaffected."
            checked={draft.isPaused}
            disabled={locked || !draft.isAutoMode}
            issue={issues.isPaused}
            onCheckedChange={(isPaused) => onChange({ isPaused })}
          />
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "edit" && !isHouse && (
          <TextField
            id={id("rate")}
            label="Commission rate (%)"
            hint={`Added on top of each box's price; 0 to ${ratePercentText(maxRateBps)}. Queued entries keep the rate they were bought at.`}
            issue={issues.rate}
          >
            <Input
              id={id("rate")}
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              value={draft.rate}
              disabled={locked}
              aria-invalid={Boolean(issues.rate)}
              onChange={(e) => onChange({ rate: e.target.value })}
            />
          </TextField>
        )}
        <TextField
          id={id("min-price")}
          label={amountInputLabel("Minimum box price", currency)}
          hint="The cheapest box that can be added or bought from outside its own; 0 for none."
          issue={issues.minBoxPrice}
        >
          <Input
            id={id("min-price")}
            type="number"
            inputMode="decimal"
            min={0}
            step={amountInputStep(currency)}
            value={draft.minBoxPrice}
            disabled={locked}
            aria-invalid={Boolean(issues.minBoxPrice)}
            onChange={(e) => onChange({ minBoxPrice: e.target.value })}
          />
        </TextField>
        <TextField
          id={id("queue-cap")}
          label="Queue limit"
          hint="Queued entries, every buyer together: 5 to 2,000."
          issue={issues.queueCap}
        >
          <Input
            id={id("queue-cap")}
            type="number"
            inputMode="numeric"
            min={5}
            max={2000}
            step={1}
            value={draft.queueCap}
            disabled={locked}
            aria-invalid={Boolean(issues.queueCap)}
            onChange={(e) => onChange({ queueCap: e.target.value })}
          />
        </TextField>
        <TextField
          id={id("buyer-cap")}
          label="Per-buyer limit"
          hint="Queued entries one buyer may hold: 1 to 50, blank for no limit."
          issue={issues.buyerQueueCap}
        >
          <Input
            id={id("buyer-cap")}
            type="number"
            inputMode="numeric"
            min={1}
            max={50}
            step={1}
            placeholder="No limit"
            value={draft.buyerQueueCap}
            disabled={locked}
            aria-invalid={Boolean(issues.buyerQueueCap)}
            onChange={(e) => onChange({ buyerQueueCap: e.target.value })}
          />
        </TextField>
        <TextField
          id={id("delay")}
          label="Stage delay (seconds)"
          hint="Holds viewers' stage back to match the stream's latency: 0 to 30."
          issue={issues.openerDelay}
        >
          <Input
            id={id("delay")}
            type="number"
            inputMode="decimal"
            min={0}
            max={30}
            step={0.01}
            value={draft.openerDelay}
            disabled={locked}
            aria-invalid={Boolean(issues.openerDelay)}
            onChange={(e) => onChange({ openerDelay: e.target.value })}
          />
        </TextField>
        <TextField
          id={id("twitch")}
          label="Twitch channel"
          hint="The login streamed beside it; blank for none. Needs the environment's Twitch app."
          issue={undefined}
        >
          <Input
            id={id("twitch")}
            value={draft.twitchChannel}
            autoComplete="off"
            placeholder="None"
            maxLength={25}
            disabled={locked}
            onChange={(e) => onChange({ twitchChannel: e.target.value })}
          />
        </TextField>
        {isHouse && (
          <TextField
            id={id("ends-at")}
            label="Ends at"
            hint="Optional. Once it passes the live break stops taking purchases, opens what was bought and closes. Clearing a passed end undoes the drain."
            issue={issues.endsAt}
          >
            <div className="flex gap-2">
              <Input
                id={id("ends-at")}
                type="datetime-local"
                value={draft.endsAt}
                disabled={disabled}
                aria-invalid={Boolean(issues.endsAt)}
                onChange={(e) => onChange({ endsAt: e.target.value })}
              />
              {draft.endsAt !== "" && !disabled && (
                <Button type="button" variant="outline" onClick={() => onChange({ endsAt: "" })}>
                  Clear
                </Button>
              )}
            </div>
          </TextField>
        )}
      </div>
    </div>
  );
}
